// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

// IncrementalMarkdown — streaming-optimized markdown renderer.
//
// Instead of re-parsing the WHOLE message with react-markdown on every
// typewriter frame (~30 fps — the main source of streaming jank on long
// answers), the text is split into blocks (paragraphs, code fences, tables).
// Every block is memoized: once a block is complete its string never changes,
// so React skips it entirely and only the trailing (incomplete) block is
// re-parsed per frame. Parsing cost stays constant instead of growing with
// the message length.
//
// Drop-in replacement for StandaloneMarkdown (same `text` prop, same
// components/styling — it reuses markdownComponents from markdown-text.jsx).
// ROLLBACK: in thread.jsx TypewriterText, use StandaloneMarkdown again and
// delete this file. Nothing else references it.

import { memo } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { markdownComponents } from "./markdown-text";

// Split raw markdown into blocks on blank lines, keeping code fences intact
// (an unterminated fence absorbs everything up to EOF, so the active block
// stays one piece until the fence closes).
const splitMarkdownBlocks = (src) => {
  const lines = src.split("\n");
  const blocks = [];
  let current = [];
  let fence = null; // fence marker (``` / ~~~) while inside a code fence

  for (const line of lines) {
    if (fence) {
      current.push(line);
      if (line.trimStart().startsWith(fence)) fence = null;
      continue;
    }
    const marker = line.match(/^\s*(```+|~~~+)/);
    if (marker) {
      fence = marker[1];
      current.push(line);
      continue;
    }
    if (line.trim() === "") {
      if (current.length > 0) {
        blocks.push(current.join("\n"));
        current = [];
      }
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) blocks.push(current.join("\n"));
  return blocks;
};

// Consecutive list blocks separated by blank lines are merged back together so
// they render as ONE list (react-markdown would render separate <ul>s with
// doubled margins otherwise).
const LIST_MARKER = /^\s*(?:[-*+]\s|\d+[.)]\s)/;

const groupBlocks = (blocks) => {
  const groups = [];
  for (const block of blocks) {
    const prev = groups[groups.length - 1];
    if (prev && LIST_MARKER.test(prev) && LIST_MARKER.test(block)) {
      groups[groups.length - 1] = `${prev}\n\n${block}`;
    } else {
      groups.push(block);
    }
  }
  return groups;
};

// Memoized on the markdown string: identical string → zero work. Completed
// blocks never change, so react-markdown never runs on them again during the
// rest of the stream.
const MemoBlock = memo(({ markdown }) => (
  <Markdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
    {markdown}
  </Markdown>
));

MemoBlock.displayName = "MemoBlock";

// ChatGPT-style "materializing" fade: while streaming, the bottom ~0.6em of
// the text (the growing edge) is partially transparent, so new words fade in
// instead of popping. The zone is deliberately SHORT (half a line): words
// become fully solid within a few further words — a full-line zone made older
// words of the current line stay "milky" for too long. A vertical gradient
// only affects the LAST line — safe with markdown, no per-word spans needed.
// Removed when the stream ends.
const FADE_MASK =
  "linear-gradient(to bottom, black calc(100% - 0.6em), rgba(0,0,0,0.5) 100%)";

const IncrementalMarkdownImpl = ({ text, fade = false }) => {
  if (!text) return null;
  const groups = groupBlocks(splitMarkdownBlocks(text));
  return (
    <div
      className="aui-md"
      style={fade ? { WebkitMaskImage: FADE_MASK, maskImage: FADE_MASK } : undefined}
    >
      {groups.map((markdown, i) => (
        <MemoBlock key={i} markdown={markdown} />
      ))}
    </div>
  );
};

export const IncrementalMarkdown = memo(IncrementalMarkdownImpl);
